import { AnalyticsService } from '@alison/analytics/client';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { createFileRoute, useBlocker } from '@tanstack/react-router';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { AppShell } from '@/components/AppShell.js';
import type { ScopeState } from '@/components/filters/types.js';
import { useScopeOptions } from '@/components/filters/useScopeOptions.js';
import { useScopeSelection } from '@/components/filters/useScopeSelection.js';
import { WorkflowsFilterBar } from '@/components/filters/WorkflowsFilterBar.js';
import { ConfirmDialog } from '@/components/settings/ConfirmDialog.js';
import {
  useAlisonEntry,
  useAlisonResultsOpen,
  useAlisonResultsRun,
} from '@/components/workflows/alison-run/AlisonRunProvider.js';
import { AlisonDoneBanner } from '@/components/workflows/alison-run/ui/AlisonDoneBanner.js';
import { runFilters } from '@/components/workflows/runFilters.js';
import { WorkflowDetail } from '@/components/workflows/WorkflowDetail.js';
import { dateWindowFromRange } from '@/lib/dateUtils.js';
import {
  filterStateFromSearch,
  useFilterSearch,
  validateFilterSearch,
} from '@/lib/filter-search.js';
import {
  mergeScopeIntoSearch,
  scopeFromSearch,
  scopeSearchShape,
  searchHasScope,
} from '@/lib/scope-search.js';
import { useAuthenticatedSession } from '@/lib/useAuthenticatedSession.js';
import { useShellAccount } from '@/lib/useShellAccount.js';

function WorkflowDetailPage(): ReactNode {
  const { workflowId } = Route.useParams();
  const search = Route.useSearch();
  const goal = search.goal ?? null;
  const resultsOpen = useAlisonResultsOpen(workflowId);
  const resultsRunId = useAlisonResultsRun(workflowId);
  // Read, not a dependency: an open reports the goal it started with; clearing it is not a new open.
  const latestGoal = useRef(goal);
  latestGoal.current = goal;
  useEffect(() => {
    const startingGoal = latestGoal.current;
    AnalyticsService.track('Workflow - Opened', {
      workflowId,
      ...(startingGoal === null ? {} : { goal: startingGoal }),
    });
  }, [workflowId]);
  const navigate = Route.useNavigate();
  const { session, isPending, onSignOut } = useAuthenticatedSession();
  const account = useShellAccount(session?.user.email);
  const scopeSearch = useMemo(() => scopeFromSearch(search), [search]);
  const hasScope = searchHasScope(search);
  const setUrlScope = (next: ScopeState): void => {
    void navigate({
      replace: true,
      search: (previous) => mergeScopeIntoSearch(previous, next),
    });
  };
  const { scope, applyScope, accountIds } = useScopeSelection({
    scope: scopeSearch,
    onScopeChange: setUrlScope,
    urlHasScope: hasScope,
  });
  const { platformOptions } = useScopeOptions(scope);
  const { added } = useFilterSearch();
  const filters = useMemo(() => runFilters(added, scope.creativeType), [added, scope.creativeType]);
  const [lockedSearch, setLockedSearch] = useState<Record<string, unknown> | null>(
    search.run === undefined ? null : (search as Record<string, unknown>),
  );
  const [pendingScope, setPendingScope] = useState<ScopeState | null>(null);
  // Bumped on start-over so the detail remounts and drops everything the earlier run picked.
  const [attempt, setAttempt] = useState(0);
  // A finished Alison run reopens the detail on the run it ended on, to continue by hand, once
  // ?run= has landed: the detail reads its run only at mount. Locked like a run started here.
  const runId = search.run ?? null;
  const latestSearch = useRef(search);
  latestSearch.current = search;
  useEffect(() => {
    if (resultsRunId === null || runId !== resultsRunId) return;
    setLockedSearch(latestSearch.current as Record<string, unknown>);
    setAttempt((count) => count + 1);
  }, [resultsRunId, runId]);
  const started = lockedSearch !== null;
  const filtersChanged =
    lockedSearch !== null &&
    JSON.stringify(runFilters(filterStateFromSearch(lockedSearch).added, scope.creativeType)) !==
      JSON.stringify(filters);
  const warn = pendingScope !== null || filtersChanged;
  const changeScope = (next: ScopeState): void => {
    if (started) {
      setPendingScope(next);
      return;
    }
    applyScope(next);
  };
  const keepScope = (): void => {
    setPendingScope(null);
    if (filtersChanged && lockedSearch !== null)
      void navigate({ replace: true, search: { ...lockedSearch, goal: search.goal } });
  };
  const startOver = (): void => {
    const next = pendingScope ?? scope;
    setPendingScope(null);
    setLockedSearch(null);
    setAttempt((count) => count + 1);
    void navigate({
      to: '/workflows/$workflowId',
      params: { workflowId },
      replace: true,
      search: (previous) =>
        mergeScopeIntoSearch({ ...previous, goal: undefined, run: undefined }, next),
    });
  };
  const dateWindow = useMemo(() => dateWindowFromRange(scope.range, new Date()), [scope.range]);
  const alisonSetup = useMemo(
    () => ({
      scope: {
        dateRange: dateWindow,
        ...(accountIds.length > 0 ? { accountIds } : {}),
        ...(Object.keys(filters).length > 0 ? { filters } : {}),
      },
      leadingKpi: scope.leadingKpi,
    }),
    [dateWindow, accountIds, filters, scope.leadingKpi],
  );
  const alisonSearch = useMemo(
    () => ({ ...(search as Record<string, unknown>), run: undefined }),
    [search],
  );
  useAlisonEntry(workflowId, goal, alisonSetup, alisonSearch);
  const setRunId = (next: string | null): void => {
    void navigate({
      replace: true,
      search: (previous) => ({
        ...previous,
        ...(next === null ? { run: undefined } : { run: next }),
      }),
    });
  };
  const clearGoal = useCallback((): void => {
    void navigate({ replace: true, search: (previous) => ({ ...previous, goal: undefined }) });
  }, [navigate]);
  const leaving = useBlocker({
    shouldBlockFn: ({ current, next }) => current.routeId !== next.routeId,
    enableBeforeUnload: false,
    disabled: runId === null,
    withResolver: true,
  });

  if (isPending || session === null) {
    return (
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AppShell
      active="workflows"
      account={account}
      onLogout={() => void onSignOut()}
      topBar={<WorkflowsFilterBar bar="workflows" scope={scope} onScopeChange={changeScope} />}
      sx={{ p: 0, display: 'flex', flexDirection: 'column' }}
    >
      {resultsOpen && <AlisonDoneBanner />}
      <WorkflowDetail
        key={attempt}
        workflowId={workflowId}
        runId={runId}
        onRunChange={setRunId}
        goal={goal}
        onGoalClear={clearGoal}
        dateWindow={dateWindow}
        accountIds={accountIds}
        availablePlatforms={platformOptions.map((platform) => platform.id)}
        leadingKpi={scope.leadingKpi}
        filters={filters}
        onRunStarted={() => setLockedSearch(search as Record<string, unknown>)}
      />

      <ConfirmDialog
        open={leaving.status === 'blocked'}
        title="Leave this workflow?"
        message="Your analysis keeps running if you leave. Find it under Workflows → Runs. The creatives you picked on this page won't be saved."
        confirmLabel="Leave"
        cancelLabel="Stay"
        isPending={false}
        onCancel={() => leaving.reset?.()}
        onConfirm={() => leaving.proceed?.()}
      />

      <ConfirmDialog
        open={warn}
        title="Change the scope?"
        message="This workflow has already run. Changing the scope starts it over from the beginning."
        confirmLabel="Change and start over"
        isPending={false}
        onCancel={keepScope}
        onConfirm={startOver}
      />
    </AppShell>
  );
}

const runSearchSchema = z
  .object({
    run: z.uuid().optional().catch(undefined),
    goal: z.string().min(1).optional().catch(undefined),
  })
  .catch({});

export const Route = createFileRoute('/workflows/$workflowId/')({
  validateSearch: (search: Record<string, unknown>) => ({
    ...validateFilterSearch(search),
    ...z.object(scopeSearchShape).catch({}).parse(search),
    ...runSearchSchema.parse(search),
  }),
  component: WorkflowDetailPage,
});
